import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import useRefreshAuth from '../../../../hooks/useRefreshAuth';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import InputField from '../../../../components/InputField';
import SelectTwo from '../../../../components/SelectTwo';
import UsageBar from '../../components/UsageBar';
import { Member, OrganizationRole, PlanLimits, PlanUsage, ROLE_OPTIONS, formatDate } from '../../types';
import { OrganizationContextProps } from '../index';

const ROLE_BADGE: Record<string, string> = { admin: 'bg-primary', lawyer: 'bg-info', assistant: 'bg-secondary' };
const EMPTY_INVITE = { email: '', first_name: '', last_name: '', role: 'lawyer' };

const TeamTab: React.FC<OrganizationContextProps> = ({ organization, membership, goToTab, reload }) => {
    const {
        getMembers, inviteMember, resendInvitation, changeMemberRole, removeMember, getRoles,
        fetchingMembers, fetchingInvite, fetchingMemberAction
    } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();
    const { refreshAuth } = useRefreshAuth();

    const [members, setMembers] = useState<Member[]>([]);
    const [limits, setLimits] = useState<PlanLimits | null>(null);
    const [usage, setUsage] = useState<PlanUsage | null>(null);
    const [showInvite, setShowInvite] = useState(false);
    const [invite, setInvite] = useState(EMPTY_INVITE);
    const [roleMember, setRoleMember] = useState<Member | null>(null);
    const [newRole, setNewRole] = useState('lawyer');
    // Roles base + roles propios del estudio (value 'custom:<id>')
    const [roleOptions, setRoleOptions] = useState<{ value: string; label: string }[]>(ROLE_OPTIONS);

    const isFirm = organization.organization_type === 'firm';
    const canManage = membership.is_admin && organization.status === 'active';

    const load = () => {
        getMembers({
            onSuccess: (response) => {
                setMembers(response.data.members);
                setLimits(response.data.limits);
                setUsage(response.data.usage);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    const loadRoles = () => {
        getRoles({
            onSuccess: (response) => {
                const custom: OrganizationRole[] = response.data.roles || [];
                setRoleOptions([
                    ...ROLE_OPTIONS,
                    ...custom.map((role) => ({ value: role.value, label: role.name }))
                ]);
            }
        });
    };

    useEffect(() => {
        load();
        if (isFirm) loadRoles();
    }, []);

    const seatsFull = !!limits && !!usage && limits.max_seats !== -1 && usage.seats >= limits.max_seats;

    const handleInvite = () => {
        inviteMember(invite, {
            onSuccess: (response) => {
                toast.success(response.message);
                setErrors({});
                setShowInvite(false);
                setInvite(EMPTY_INVITE);
                load();
                reload();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error('Revisa los datos de la invitación');
            }
        });
    };

    const handleResend = (member: Member) => {
        resendInvitation(member.id, {
            onSuccess: (response) => {
                toast.success(response.message);
                load();
            },
            onError: (response) => toast.error(response.message)
        });
    };

    const handleChangeRole = () => {
        if (!roleMember) return;
        changeMemberRole(roleMember.id, newRole, {
            onSuccess: (response) => {
                toast.success(response.message);
                setRoleMember(null);
                load();
                refreshAuth();
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => toast.error(response.data?.role?.[0] || 'Rol no válido')
        });
    };

    const handleRemove = (member: Member) => {
        const invited = member.status === 'invited';
        requestConfirmation({
            title: invited ? '¿Anular la invitación?' : `¿Retirar a ${member.name} del estudio?`,
            message: invited
                ? `La invitación a ${member.email} dejará de funcionar y se libera el asiento.`
                : 'Dejará de ver los casos y clientes del estudio de inmediato. Sus casos en curso quedarán sin abogado para que los reasignes.',
            buttons: {
                confirmButton: {
                    label: invited ? 'Sí, anular' : 'Sí, retirar',
                    onClick: () => {
                        removeMember(member.id, {
                            onSuccess: (response) => {
                                toast.success(response.message);
                                load();
                                reload();
                            },
                            onError: (response) => toast.error(response.message)
                        });
                    }
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    return (
        <DefaultCard>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div style={{ minWidth: 260, flex: 1 }}>
                    <h5 className="mb-1">{isFirm ? 'Equipo del estudio' : 'Usuarios'}</h5>
                    <p className="text-muted small mb-2">
                        {isFirm
                            ? 'Cada rol define qué puede hacer la persona. Las invitaciones pendientes ocupan un asiento.'
                            : 'Tu plan de abogado independiente es para un solo usuario.'}
                    </p>
                    {limits && usage && <UsageBar label="Asientos" used={usage.seats} limit={limits.max_seats} />}
                </div>
                {canManage && isFirm && (
                    <ButtonGeneral
                        onClick={() => {
                            setErrors({});
                            setShowInvite(true);
                        }}
                        title="Invitar"
                        icon="fa-user-plus"
                        size="small"
                        disabled={seatsFull}
                    />
                )}
            </div>

            {isFirm && seatsFull && canManage && (
                <div className="alert alert-info small">
                    Usaste todos los asientos de tu plan.{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => goToTab('subscription')}>
                        Mejora tu plan
                    </button>{' '}
                    o retira a alguien para invitar.
                </div>
            )}
            {!isFirm && (
                <div className="alert alert-light border small">
                    ¿Quieres trabajar con un equipo? Los planes Estudio permiten sumar abogados y asistentes. Escríbenos y
                    convertimos tu cuenta.
                </div>
            )}

            {fetchingMembers && members.length === 0 ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle" data-testid="members-table">
                        <thead>
                            <tr>
                                <th>Persona</th>
                                <th>Rol</th>
                                <th>Estado</th>
                                <th>Desde</th>
                                {canManage && <th className="text-center">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((member) => (
                                <tr key={member.id}>
                                    <td>
                                        <span className="fw-bold d-block">{member.name}{member.is_owner && <i className="fas fa-crown text-warning ms-2" title="Dueño" />}</span>
                                        <small className="text-muted">{member.email}</small>
                                    </td>
                                    <td><span className={`badge ${ROLE_BADGE[member.base_role || member.role] || 'bg-dark'}`}>{member.role_display}</span></td>
                                    <td>
                                        <span className={`badge ${member.status === 'active' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                            {member.status_display}
                                        </span>
                                        {member.status === 'invited' && member.invitation_expires_at && (
                                            <small className="d-block text-muted">Vence {formatDate(member.invitation_expires_at)}</small>
                                        )}
                                    </td>
                                    <td><small>{formatDate(member.joined_at || member.invitation_sent_at)}</small></td>
                                    {canManage && (
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-1">
                                                {member.status === 'invited' && (
                                                    <ButtonTableAction callbackFunction={() => handleResend(member)} title="Reenviar invitación"
                                                                       classIcon="fa-paper-plane" colorIcon="text-info" sizeIcon="16" />
                                                )}
                                                {!member.is_owner && (
                                                    <ButtonTableAction
                                                        callbackFunction={() => {
                                                            setRoleMember(member);
                                                            setNewRole(member.role);
                                                        }}
                                                        title="Cambiar rol" classIcon="fa-user-tag" colorIcon="text-warning" sizeIcon="16" />
                                                )}
                                                {!member.is_owner && (
                                                    <ButtonTableAction callbackFunction={() => handleRemove(member)}
                                                                       title={member.status === 'invited' ? 'Anular invitación' : 'Retirar'}
                                                                       classIcon="fa-user-minus" colorIcon="text-danger" sizeIcon="16" />
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DefaultModal
                title="Invitar al equipo"
                show={showInvite}
                handleClose={() => setShowInvite(false)}
                actionButton={<ButtonSaveForm callbackSave={handleInvite} loading={fetchingInvite} title="Enviar invitación" />}
            >
                <div className="row">
                    <div className="col-12 mb-3">
                        <InputField label="Correo" type="email" value={invite.email} required
                                    onChange={(value) => setInvite({ ...invite, email: value })}
                                    onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Nombre" value={invite.first_name}
                                    onChange={(value) => setInvite({ ...invite, first_name: value })}
                                    onFocus={() => onFocusRemove('first_name')} error={fieldErrorMessage('first_name')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Apellido" value={invite.last_name}
                                    onChange={(value) => setInvite({ ...invite, last_name: value })}
                                    onFocus={() => onFocusRemove('last_name')} error={fieldErrorMessage('last_name')} />
                    </div>
                    <div className="col-12 mb-2">
                        <SelectTwo label="Rol" inputValue={invite.role} options={roleOptions}
                                   onChange={(option: any) => setInvite({ ...invite, role: option?.value || 'lawyer' })}
                                   hasError={!!fieldErrorMessage('role')} />
                    </div>
                    <div className="col-12">
                        <small className="text-muted">
                            Recibirá un correo con un enlace válido por 7 días. Si no tiene cuenta, la crea al aceptar.
                        </small>
                    </div>
                </div>
            </DefaultModal>

            <DefaultModal
                title={`Cambiar rol de ${roleMember?.name || ''}`}
                show={!!roleMember}
                handleClose={() => setRoleMember(null)}
                actionButton={<ButtonSaveForm callbackSave={handleChangeRole} loading={fetchingMemberAction} title="Guardar" />}
            >
                <SelectTwo label="Nuevo rol" inputValue={newRole} options={roleOptions}
                           onChange={(option: any) => setNewRole(option?.value || newRole)} />
                <small className="text-muted d-block mt-2">
                    Además de los roles base, puedes crear roles propios del estudio en la pestaña Roles.
                </small>
            </DefaultModal>
        </DefaultCard>
    );
};

export default TeamTab;
